import React, { useContext, useEffect, useState, Suspense, lazy } from "react";
import { API, Auth } from "aws-amplify";
import { Route, Switch, useLocation, useHistory } from "react-router-dom";
import {
  FiltersWrapper,
  StyledToastContainer,
  SystemNotificationsWrapper,
  UserNotification,
  AdminRoute,
  AccessDeniedScreen
} from "@digitallab/grid-common-frontend-components";
import { MatomoProvider, createInstance } from "@datapunt/matomo-tracker-react";
import styled from "styled-components";
import { CssBaseline } from "@mui/material";
import AppBar from "./views/AppBar";
import LoadInstrumentsInfo from "./features/instruments/LoadInstrumentsInfo";
import { getEnv } from "./utils/helpers/text";
import { BackendInfoContext } from "./features/backend-info-context/context";
import { LIST_BACKEND_HEALTH_CHECKS } from "./gql/landingapi";
import AppDrawer from "./AppDrawer";
import MainPageMaintenance from "./views/MainPageMaintenance";
import TogglerContextWrapper from "../src/components/shared/TogglerContextWrapper";
import DrawerMenu from "./DrawerMenu";
import ReAuthenticationHandler from "./features/4ep/ReAuthenticationHandler";
import { LandingPageNotifications, ShortViewOfNotifyWrapper } from "@digitallab/grid-common-frontend-components";
import AWSAppSyncClient, { AUTH_TYPE } from "aws-appsync";
import { getAllData } from "./utils/helpers/fetching";
import { useSelector } from "react-redux";
import { DialogServiceContextWrapper } from "./features/instruments/dialogService/DialogServiceContextWrapper";
import { LandingPageAppSyncProvider } from "./utils/contexts/LandingPageAppSyncContext";
import { ADMIN_ROLE } from "./constants";
import "react-toastify/dist/ReactToastify.css";
import "./App.scss";

// Route-level pages are code-split so a given route only evaluates the page it
// renders. This keeps App's import graph small (big win for test startup, where
// every test file re-evaluates whatever App statically imports).
const InstrumentsMainPage = lazy(() => import("./features/instruments/InstrumentsMainPage"));
const ExportCsvMainPage = lazy(() => import("./features/export-csv/ExportCsvMainPage"));
const TableauEquipmentPage = lazy(() => import("./features/tableau/TableauEquipmentPage"));
const ExportCsvWapper = lazy(() => import("./features/export-csv/ExportCsvWapper"));
const ImportMainView = lazy(() => import("./features/importFile/ImportMainView"));
const AdminMainView = lazy(() => import("./features/admin/AdminMainView"));

const ADMIN_PATHS = ["/admin", "/import"];

const MainContainer = styled.div`
  margin-top: 22px;
  padding: 0 20px;
  width: 100%;
`;

const currentEnv = getEnv();

const FlexBox = styled.div`
  display: flex;
  height: 100%;
  overflow: auto;
`;
const AdaptarWrapperStyle = styled.div`
  width: 100%;
  overflow: auto;
`;
const AppVal = styled.div`
  .MuiAppBar-positionAbsolute {
    top: 42px;
  }
`;
const getContainerHeight = (currentEnv) => {
  return currentEnv ? window.innerHeight - 126 : window.innerHeight - 76;
};
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    console.log("recordingError: " + error);
  }
  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div>
          <h1>Something went wrong.</h1>
          <button
            onClick={() => {
              window.location.href = "/";
            }}
          >
            Clear Error
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const { hideFooter } = useContext(BackendInfoContext);
  const [enableVal, setEnableVal] = useState(false);
  const [containerHeight, setContainerHeight] = useState(getContainerHeight(currentEnv));
  const { currentUser, maintenance, setMaintenance, dbVersionMismatch, setDbVersionMismatch } =
    useContext(BackendInfoContext);
  const userId = useSelector((state) => state.user.user);
  const userRole = useSelector((state) => state.user.actionStatus?.hasRole);
  const location = useLocation();
  const history = useHistory();
  const getBackendAuthChecks = async () => {
    try {
      const backendBuildInfo = await API.graphql({
        query: LIST_BACKEND_HEALTH_CHECKS
      });
      const items = backendBuildInfo?.data?.listBackendHealthChecks?.items;
      const trackDisable = items.map((item) => item?.enableAnalyticsTracking);
      const isAppUnderMaintenance = items.map((item) => item?.isAppUnderMaintenance);
      setMaintenance(isAppUnderMaintenance[0]);
      setDbVersionMismatch(!items[0]?.dbVersion);
      setEnableVal(trackDisable[0]);
    } catch (error) {
      console.log("Matomo api error..", error);
    }
  };
  useEffect(() => {
    const handler = () => setContainerHeight(getContainerHeight(currentEnv));
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    getBackendAuthChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Matomo code added for analytics
  // urlBase: optional, default value: `${urlBase}matomo.php`
  // siteId : optional, default value: `${urlBase}matomo.js`
  // disabled :optional, false by default. Makes all tracking calls no-ops if set to true.
  // linkTracking :optional, default value: true
  // active: optional, default value: true
  // seconds: optional, default value: `15

  const instance = createInstance({
    urlBase: process.env.REACT_APP_MATOMO_BASE_URL,
    siteId: process.env.REACT_APP_SITE_ID,
    trackerUrl: process.env.REACT_APP_MATOMO_BASE_URL + "matomo.php",
    srcUrl: process.env.REACT_APP_MATOMO_BASE_URL + "matomo.js",
    disabled: enableVal,
    linkTracking: true,
    trackPageView: true,
    heartBeat: {
      active: true,
      seconds: 10
    }
  });
  const [isInboxActive, setIsInboxActive] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);

  useEffect(() => {
    const handler = () => {
      setInboxCount((c) => c + 1);
      setIsInboxActive(true);
    };
    window.addEventListener("ir:inbox-refresh", handler);
    return () => window.removeEventListener("ir:inbox-refresh", handler);
  }, []);

  const landingPageAppSyncClient = new AWSAppSyncClient({
    url: process.env.REACT_APP_LP_AWS_GRAPHQL_ENDPOINT,
    region: process.env.REACT_APP_AWS_PROJECT_REGION,
    auth: {
      type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
      jwtToken: async () => (await Auth.currentSession()).getAccessToken().getJwtToken()
    },
    disableOffline: true
  });

  if (maintenance || dbVersionMismatch) return <MainPageMaintenance />;

  return (
    <ErrorBoundary>
      <MatomoProvider value={instance}>
        <LandingPageAppSyncProvider client={landingPageAppSyncClient}>
          <StyledToastContainer />
          <LoadInstrumentsInfo setIsInboxActive={setIsInboxActive} setInboxCount={setInboxCount}>
            <ReAuthenticationHandler user={currentUser}>
              <FiltersWrapper>
                <ShortViewOfNotifyWrapper>
                  <SystemNotificationsWrapper>
                    <CssBaseline />

                    {currentEnv ? (
                      <AppVal>
                        <AppBar
                          landingPageAppSyncClient={landingPageAppSyncClient}
                          isInboxActive={isInboxActive}
                          inboxCount={inboxCount}
                        />
                      </AppVal>
                    ) : (
                      <AppBar
                        landingPageAppSyncClient={landingPageAppSyncClient}
                        isInboxActive={isInboxActive}
                        inboxCount={inboxCount}
                      />
                    )}
                    <DialogServiceContextWrapper>
                      <TogglerContextWrapper>
                        <AdaptarWrapperStyle
                          style={
                            currentUser && currentEnv
                              ? {
                                  top: 92,
                                  height: `${hideFooter ? containerHeight : containerHeight + 23}px`
                                }
                              : {
                                  top: 50,
                                  height: `${hideFooter ? containerHeight : containerHeight + 23}px`
                                }
                          }
                        >
                          <AdminRoute
                            fallback={<AccessDeniedScreen onGoHome={() => history.replace("/")} />}
                            adminPaths={ADMIN_PATHS}
                            adminRole={ADMIN_ROLE}
                            userRole={userRole}
                            pathname={location.pathname}
                          >
                            <FlexBox>
                              <AppDrawer>
                                <DrawerMenu />
                              </AppDrawer>

                              <Suspense fallback={null}>
                                <Switch>
                                  <Route exact path="/">
                                    <InstrumentsMainPage />
                                  </Route>
                                  <Route exact path="/equipmentReview/:ReviewId">
                                    <InstrumentsMainPage />
                                  </Route>
                                  <Route exact path="/equipmentList/:InventoryId">
                                    <InstrumentsMainPage />
                                  </Route>
                                  <Route exact path="/import">
                                    <ImportMainView />
                                  </Route>
                                  <Route exact path="/admin">
                                    <AdminMainView />
                                  </Route>
                                  <Route exact path="/tableau-equipments">
                                    <TableauEquipmentPage />
                                  </Route>
                                  <Route exact path="/export-csv">
                                    <ExportCsvWapper>
                                      <ExportCsvMainPage
                                        setIsInboxActive={setIsInboxActive}
                                        isInboxActive={isInboxActive}
                                        setInboxCount={setInboxCount}
                                      />
                                    </ExportCsvWapper>
                                  </Route>
                                  <Route exact path="/notifications">
                                    <MainContainer>
                                      <LandingPageNotifications
                                        userId={userId}
                                        getAllData={getAllData}
                                        id={"all"}
                                        landingPageAppSyncClient={landingPageAppSyncClient}
                                      />
                                    </MainContainer>
                                  </Route>
                                </Switch>
                              </Suspense>
                            </FlexBox>
                          </AdminRoute>
                        </AdaptarWrapperStyle>
                      </TogglerContextWrapper>
                      <UserNotification landingPageAppSyncClient={landingPageAppSyncClient} userId={userId} />
                    </DialogServiceContextWrapper>
                  </SystemNotificationsWrapper>
                </ShortViewOfNotifyWrapper>
              </FiltersWrapper>
            </ReAuthenticationHandler>
          </LoadInstrumentsInfo>
        </LandingPageAppSyncProvider>
      </MatomoProvider>
    </ErrorBoundary>
  );
}

export default App;
